// BevakningEditorModal — create or edit a saved job search ("Bevakning").
// Bevakningar 2.0: a bevakning has a candidate-given NAME plus the free-text
// query and the structured filter facets (reusing the same JobFilterBar the job
// board uses). One modal serves three entry points:
//   • "Ny bevakning" on the Bevakningar tab   → blank create
//   • "Redigera" on a bevakning card          → edit existing
//   • "Spara som bevakning" on the job board  → create pre-seeded with the
//     current board search (query + filters), so naming is the only step.
import React, { useEffect, useMemo, useState } from 'react';
import { X, Bell } from 'lucide-react';
import { jobApplicationApi } from '@/lib/api/jobApplicationApi';
import { needSubscriptionApi } from '@/lib/api/needSubscriptionApi';
import { NeedSubscription } from '@/lib/models/needSubscription';
import {
    EMPTY_JOB_FILTERS,
    filtersToCriteria,
    criteriaToFilters,
    criteriaChips,
    deriveBevakningName,
    type JobFilters,
    type JobSort,
} from '@/lib/jobMatching';
import { IslandSlideOver } from '@/components/custom-ui/IslandSlideOver';
import { ActionButton } from '@/components/custom-ui/action-button';
import JobFilterBar from './JobFilterBar';

interface BevakningEditorModalProps {
    existing?: NeedSubscription | null;
    initialQuery?: string;
    initialFilters?: JobFilters;
    onClose: () => void;
    onSaved: (s: NeedSubscription) => void;
}

const BevakningEditorModal: React.FC<BevakningEditorModalProps> = ({
    existing = null,
    initialQuery = '',
    initialFilters,
    onClose,
    onSaved,
}) => {
    const isEdit = !!existing;
    const [name, setName] = useState(existing?.name ?? '');
    const [query, setQuery] = useState(existing ? existing.query : initialQuery);
    const [filters, setFilters] = useState<JobFilters>(
        existing ? criteriaToFilters(existing.criteria) : (initialFilters ?? EMPTY_JOB_FILTERS),
    );
    // Sort isn't part of a bevakning; JobFilterBar just needs a value + setter.
    const [sort, setSort] = useState<JobSort>('recent');
    const [cityOptions, setCityOptions] = useState<string[]>([]);
    const [competencyOptions, setCompetencyOptions] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        jobApplicationApi
            .getNeedFacets()
            .then((f) => {
                setCityOptions(f.cities);
                setCompetencyOptions(f.competencies);
            })
            .catch(() => {
                /* facets are best-effort — the free-text + toggles still work */
            });
    }, []);

    const criteria = useMemo(() => filtersToCriteria(filters), [filters]);
    const suggestion = deriveBevakningName(query, criteria);
    const chips = criteriaChips(query, criteria);

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);
        setError(null);
        try {
            const saved = isEdit
                ? await needSubscriptionApi.updateSubscription(existing!.id, name.trim(), query.trim(), criteria)
                : await needSubscriptionApi.createSubscription(name.trim(), query.trim(), criteria);
            onSaved(saved);
        } catch (err: any) {
            setError(err?.message || 'Kunde inte spara bevakningen. Försök igen.');
            setSaving(false);
        }
    };

    return (
        <IslandSlideOver onClose={onClose} widthClass="max-w-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
                <div>
                    <h2 className="text-lg font-bold text-gray-900">
                        {isEdit ? 'Redigera bevakning' : 'Ny bevakning'}
                    </h2>
                    <p className="text-sm text-gray-500">
                        Få en notis så snart en ny tjänst matchar din sökning.
                    </p>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5 text-gray-500" />
                </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Name */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Namn på bevakningen
                    </label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={suggestion || 'T.ex. Heltid, Socionom i Göteborg'}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D4F]"
                    />
                    {!name.trim() && suggestion && (
                        <button
                            type="button"
                            onClick={() => setName(suggestion)}
                            className="mt-2 text-xs text-[#1A4D4F] hover:underline underline-offset-2"
                        >
                            Använd förslag: “{suggestion}”
                        </button>
                    )}
                </div>

                {/* Free-text */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sökord <span className="font-normal text-gray-400">(valfritt)</span>
                    </label>
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Titel, roll, stad eller kompetens…"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D4F]"
                    />
                </div>

                {/* Filters */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Filter</label>
                    <JobFilterBar
                        filters={filters}
                        onFiltersChange={setFilters}
                        sort={sort}
                        onSortChange={setSort}
                        cityOptions={cityOptions}
                        competencyOptions={competencyOptions}
                        resultCount={0}
                    />
                </div>

                {/* Live summary */}
                <div className="rounded-xl border border-[#A4C3B2]/50 bg-[#A4C3B2]/10 p-4">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-[#1A4D4F] mb-2">
                        <Bell className="w-3.5 h-3.5" /> Du blir notifierad om tjänster som matchar:
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {chips.map((c, i) => (
                            <span
                                key={`${c}-${i}`}
                                className="inline-flex items-center rounded-full bg-white border border-[#A4C3B2]/60 px-2.5 py-1 text-xs font-medium text-[#1A4D4F]"
                            >
                                {c}
                            </span>
                        ))}
                    </div>
                </div>

                {error && <p className="text-sm text-red-600">{error}</p>}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-100 flex-shrink-0">
                <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl"
                >
                    Avbryt
                </button>
                <ActionButton icon={Bell} onClick={handleSave} disabled={saving}>
                    {saving ? 'Sparar…' : isEdit ? 'Spara ändringar' : `Skapa bevakning`}
                </ActionButton>
            </div>
        </IslandSlideOver>
    );
};

export default BevakningEditorModal;
